import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress
} from '@mui/material';
import { NotificationsActive } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAppTheme } from '@contexts/ThemeContext';
import { useAuth } from '@store/authStore';
import {
    getNotificationPreferences,
    updateNotificationPreferences,
    NotificationPreferences as Preferences
} from '@services/UserNotificationPreferencesService';

type ToggleKey = keyof Preferences;

interface ToggleDef {
    key: ToggleKey;
    label: string;
    description: string;
    hrManagerOnly?: boolean;
}

const TOGGLES: ToggleDef[] = [
    {
        key: 'attendanceReminders',
        label: 'Attendance reminders',
        description: 'Punch-in and punch-out reminder emails sent to you.'
    },
    {
        key: 'leaveStatusEmails',
        label: 'Leave status updates',
        description: 'Emails when your leave request is approved or declined.'
    },
    {
        key: 'absenceAlerts',
        label: 'Absence alerts',
        description: 'Emails notifying you when a team member has no attendance recorded.',
        hrManagerOnly: true
    },
    {
        key: 'leaveAdminAlerts',
        label: 'Leave admin alerts',
        description: 'Emails when an employee withdraws or edits a leave request.',
        hrManagerOnly: true
    }
];

const NotificationPreferences: React.FC = () => {
    const { user } = useAuth();
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();

    const [prefs, setPrefs] = useState<Preferences | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState<ToggleKey | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const role = (user?.role || '').toLowerCase();
    const isHrOrManager =
        role === 'hr' ||
        role === 'admin' ||
        user?.is_super_admin === 1 ||
        Boolean(user?.is_manager);

    const visibleToggles = TOGGLES.filter(t => !t.hrManagerOnly || isHrOrManager);

    useEffect(() => {
        let active = true;
        const load = async () => {
            if (!user?.id) return;
            setLoading(true);
            const res = await getNotificationPreferences(user.id);
            if (!active) return;
            if (res.success && res.data) {
                setPrefs(res.data);
            } else {
                setError(res.error?.message || 'Failed to load notification preferences');
            }
            setLoading(false);
        };
        load();
        return () => {
            active = false;
        };
    }, [user?.id]);

    const handleToggle = async (key: ToggleKey, value: boolean) => {
        if (!user?.id || !prefs) return;
        const previous = prefs;
        // Optimistic update
        setPrefs({ ...prefs, [key]: value });
        setSavingKey(key);
        setError('');
        setSuccess('');

        const res = await updateNotificationPreferences(user.id, { [key]: value });
        setSavingKey(null);

        if (res.success && res.data) {
            setPrefs(res.data);
            setSuccess('Preferences updated');
            setTimeout(() => setSuccess(''), 2500);
        } else {
            // Revert on failure
            setPrefs(previous);
            setError(res.error?.message || 'Failed to update preferences');
            setTimeout(() => setError(''), 4000);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <NotificationsActive sx={{ color: '#1976d2' }} />
                    <Typography
                        variant="h6"
                        sx={{ color: theme.palette.text.primary, fontWeight: 600, fontSize: '1.1rem' }}
                    >
                        Notification Preferences
                    </Typography>
                </Box>
                <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, mb: 3 }}
                >
                    Choose which automated emails you want to receive. Turning one off stops
                    those emails for your account. (Account-critical emails like password reset,
                    welcome and salary slips are always sent.)
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                {loading || !prefs ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    visibleToggles.map(toggle => (
                        <Box
                            key={toggle.key}
                            sx={{
                                mb: 2,
                                p: 3,
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                                borderRadius: 2,
                                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                                }
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={prefs[toggle.key]}
                                        disabled={savingKey === toggle.key}
                                        onChange={(e) => handleToggle(toggle.key, e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#1976d2',
                                                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#1976d2'
                                            },
                                            '& .MuiSwitch-track': {
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
                                            }
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            variant="body1"
                                            sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 0.5 }}
                                        >
                                            {toggle.label}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }}
                                        >
                                            {toggle.description}
                                        </Typography>
                                    </Box>
                                }
                                sx={{ alignItems: 'flex-start', m: 0 }}
                            />
                        </Box>
                    ))
                )}
        </Box>
    );
};

export default NotificationPreferences;
